import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PageHero, Container, Overline } from "../components/Section";
import { api, formatApiError } from "../lib/api";
import { HandHelping, Heart, Users, Sparkles, Send, ShieldCheck } from "lucide-react";

const EMPTY = {
  name: "",
  prayer_request: "",
  additional_info: "",
  phone: "",
  email: "",
  is_private: false,
};

export default function PrayerRequest() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const u = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => setForm((f) => ({ ...f, [k]: !f[k] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.prayer_request.trim()) {
      toast.error("Please share your prayer request.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([k, v]) => k === "is_private" || (v !== "" && v !== null))
      );
      await api.post("/prayer-requests", payload);
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="prayer-request-page">
      <PageHero
        overline="🙏🏾 September is Prayer Month"
        title={<>Agreeing together in <em className="text-gold-light italic font-light">prayer</em>.</>}
        kicker="One heart. One voice. One God. Share your prayer request and we will agree with you in prayer."
        image="/images/branding/pastor-worship.jpg"
      />

      <Container className="pb-32">
        {/* Announcement banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative glass p-10 md:p-14 mb-16 overflow-hidden"
          data-testid="prayer-announcement"
        >
          <div className="absolute top-0 left-0 h-1 w-32 bg-gold" />
          <div className="grid md:grid-cols-3 gap-10 items-start">
            <div className="md:col-span-2">
              <Overline>Prayer Month</Overline>
              <h2 className="font-sans text-3xl md:text-4xl font-black leading-tight tracking-[-0.02em] mb-6">
                September is <span className="brand-gradient-text">Prayer Month</span> at Sixth Mount Zion.
              </h2>
              <p className="text-white/85 leading-relaxed text-lg">
                This month, we're agreeing together in prayer — <strong className="text-gold-light">one heart, one voice, and one God.</strong> We invite you to submit your prayer requests as we pray, believe, support one another, and watch God move.
              </p>
              <p className="text-white/75 leading-relaxed mt-4">
                To our virtual congregation: fill out the form below and your request will be <strong className="text-gold-light">printed and placed on the altar</strong> along with all other prayer requests.
              </p>
              <p className="font-serif italic text-xl text-gold mt-6">
                Let's agree together in prayer for a brighter and stronger tomorrow.
              </p>
            </div>
            <div className="border-l border-white/10 md:pl-8">
              <div className="grid grid-cols-2 gap-4">
                <Tag icon={HandHelping} label="Pray" />
                <Tag icon={Sparkles}    label="Believe" />
                <Tag icon={Users}       label="Support" />
                <Tag icon={Heart}       label="See God Move" />
              </div>
              <blockquote className="mt-8 border-l-2 border-gold pl-5 italic text-white/85 leading-relaxed">
                "Again I say to you, that if two of you agree on earth about anything they ask, it will be done for them by My Father in heaven."
                <footer className="mt-3 not-italic text-xs uppercase tracking-widest text-gold font-bold">— Matthew 18:19 (NIV)</footer>
              </blockquote>
            </div>
          </div>
        </motion.div>

        {/* Form or thank-you */}
        {submitted ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="glass p-12 md:p-16 text-center"
            data-testid="prayer-thank-you"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/15 border border-gold/40 mb-6">
              <HandHelping className="h-9 w-9 text-gold" />
            </div>
            <Overline>Received</Overline>
            <h3 className="font-sans text-4xl md:text-5xl font-black mb-6 tracking-[-0.02em]">
              Your prayer request has been <span className="brand-gradient-text">received</span>.
            </h3>
            <p className="text-white/80 max-w-2xl mx-auto leading-relaxed text-lg">
              Thank you for trusting us with what's on your heart. Our prayer team is now agreeing with you, and your request will be printed and placed on the altar this month.
            </p>
            <p className="font-serif italic text-gold-light mt-6 text-lg">
              A praying people. A stronger tomorrow.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => { setForm(EMPTY); setSubmitted(false); }}
                className="btn-wine px-7 py-3.5 rounded-full text-sm uppercase tracking-widest font-bold"
                data-testid="prayer-submit-another"
              >
                Submit Another Request
              </button>
            </div>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="glass p-10 md:p-14 space-y-8" data-testid="prayer-form">
            <div>
              <Overline>Prayer Request</Overline>
              <h2 className="font-sans text-3xl md:text-4xl font-black tracking-[-0.02em]">Share what's on your heart.</h2>
              <p className="text-white/60 mt-2 text-sm">Every field except your prayer request is optional. Submit anonymously if you prefer.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
              <Field label="Name" hint="Optional — leave blank to submit anonymously">
                <input data-testid="prayer-name" value={form.name} onChange={u("name")} className={inputCls} placeholder="Your name" />
              </Field>
              <Field label="Email" hint="Optional — for pastoral follow-up">
                <input data-testid="prayer-email" type="email" value={form.email} onChange={u("email")} className={inputCls} placeholder="you@example.com" />
              </Field>
            </div>

            <Field label="Prayer Request" required hint="Share your request so we can agree together with you in prayer.">
              <textarea
                data-testid="prayer-request"
                required
                rows={6}
                value={form.prayer_request}
                onChange={u("prayer_request")}
                className={inputCls}
                placeholder="Lord, we lift up..."
              />
            </Field>

            <Field label="Additional Information" hint="Anything else the prayer team should know?">
              <textarea
                data-testid="prayer-additional"
                rows={3}
                value={form.additional_info}
                onChange={u("additional_info")}
                className={inputCls}
              />
            </Field>

            <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
              <Field label="Phone" hint="Optional — for direct follow-up">
                <input data-testid="prayer-phone" value={form.phone} onChange={u("phone")} className={inputCls} placeholder="(757) 555-0100" />
              </Field>
              <Field label="Privacy">
                <button
                  type="button"
                  onClick={() => toggle("is_private")}
                  className={`w-full text-left px-4 py-3 border transition-colors text-sm font-bold tracking-tight flex items-center gap-3 ${
                    form.is_private
                      ? "border-gold bg-gold/10 text-gold"
                      : "border-white/15 hover:border-white/40 text-white/80"
                  }`}
                  data-testid="prayer-private-toggle"
                >
                  <ShieldCheck className={`h-4 w-4 shrink-0 ${form.is_private ? "text-gold" : "text-white/40"}`} />
                  <span>{form.is_private ? "Private — Pastor & prayer team only" : "Public — share with prayer team"}</span>
                </button>
              </Field>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-white/10">
              <p className="text-xs text-white/50 italic max-w-md">
                By submitting, you agree to allow our prayer team to lift you up in prayer this month.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="btn-wine px-10 py-3.5 rounded-full text-sm uppercase tracking-widest font-bold inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="prayer-submit"
              >
                <Send className="h-4 w-4" /> {submitting ? "Submitting…" : "Submit Prayer Request"}
              </button>
            </div>
          </form>
        )}

        <p className="text-center text-xs uppercase tracking-[0.3em] text-gold font-bold mt-16">
          A Praying People. A Stronger Tomorrow.
        </p>
      </Container>
    </div>
  );
}

/* ---------- Small helpers ---------- */
const inputCls = "w-full bg-transparent border-0 border-b border-white/15 text-white px-0 py-3 focus:outline-none focus:border-gold transition-colors placeholder:text-white/30";

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-white/55 mb-2 font-semibold">
        {label}{required && <span className="text-gold ml-1">*</span>}
      </div>
      {children}
      {hint && <p className="text-xs text-white/40 mt-2">{hint}</p>}
    </label>
  );
}

function Tag({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center justify-center py-4 border border-white/10 bg-wine-900/30">
      <Icon className="h-5 w-5 text-gold mb-2" />
      <div className="text-[10px] uppercase tracking-widest text-white/75 font-bold">{label}</div>
    </div>
  );
}
